import { useMemo, useState } from 'react';
import { STANDARD_DURATIONS, EXIT_REASON_LABELS, isAutomatedExitReason, authoredExitDescription } from '@einhaus/project314-shared';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import FormCheckbox from '@/components/UI/FormCheckbox';
import FormInput from '@/components/UI/FormInput';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import { parseNumericDraft, toNumericDraft } from '@/components/UI/numericDraft';
import type { TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import {
    buildReferenceNodeInputState,
    hasImportedConditionExpression,
    parseReferenceNodeInputState,
    referenceKindOptions,
    type TradeLogicContextFeatureRef,
    type LogicActionNode,
    type LogicConditionNode,
    type LogicGroupNode,
    type LogicGroupOperator,
    type LogicNode,
    type LogicReferenceKind,
    type LogicReferenceNode,
    type LogicScheduleNode,
    type LogicSequenceMode,
    type LogicSequenceNode,
    type TradeLogicDiagnostic,
    type TradeLogicJsonObject,
    type TradeLogicJsonValue,
    type TradeLogicMissingDataPolicy,
    type TradeLogicNodeId,
    type TradeLogicTrailingMode
} from '../model';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryFeatureDto,
    TradeLogicRegistrySchemaFieldDto
} from '../registry';
import FeatureConditionValueInput from './FeatureConditionValueInput';
import SchemaFieldInput from './SchemaFieldInput';
import SequenceStepTimingEditor from './SequenceStepTimingEditor';
import SchemaFieldsEditor from './SchemaFieldsEditor';
import {
    buildConditionTemplateValueField,
    buildFeatureFilters,
    buildFilterInputState,
    buildParsedSchemaValues,
    buildSchemaInputValues,
    buildTrailingInputState,
    conditionValueToInputString,
    parseConditionValue,
    parseSchemaInputValue,
    parseTrailingInputState,
    schemaFieldValueToInputString,
    schemaValueToInputString,
    type TrailingInputState
} from './schemaControlUtils';
import {
    buildScheduleParameterInputState,
    parseScheduleParameterInputState,
    scheduleDayOfWeekOptions,
    scheduleSessionOptions,
    toggleScheduleDayOfWeek
} from './scheduleNodeParameters';
import {
    buildQuantityExpressionInputState,
    createLiteralQuantityExpression,
    parseQuantityExpressionInputState,
    type QuantityExpressionInputState,
    type QuantityExpressionMode
} from './actionQuantityExpression';
import { buildEntryActionTemplateDefaultValues, hasEntryActionTemplateDefaults } from './actionTemplateDefaults';
import { resolveInitialConditionOperator } from './conditionOperatorDefaults';
import PriceExecutionEditor from './PriceExecutionEditor';
import { withPriceExecution } from './priceExecutionDraft';
import {
    collectSequenceStepPriceCaptureSources,
    findSequencePriceCaptureSource,
    withSequenceStepCapture,
    type SequencePriceCaptureTargetOption
} from './sequencePriceCaptures';

interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface NodeInspectorPanelProps {
    node: LogicNode | null;
    priceWaitAvailable: boolean;
    priceCaptureOptions: SequencePriceCaptureTargetOption[];
    diagnostics: TradeLogicDiagnostic[];
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
    definitionOptions: SelectOption[];
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
    featureRefValidationState?: 'idle' | 'checking' | 'valid' | 'invalid' | 'error';
    onLabelChange: (nodeId: TradeLogicNodeId, label: string) => void;
    onGroupChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicGroupNode>) => void;
    onSequenceChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicSequenceNode>) => void;
    onScheduleChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicScheduleNode>) => void;
    onReferenceChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicReferenceNode>) => void;
    onConditionChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicConditionNode>) => void;
    onActionChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicActionNode>) => void;
    onToggleNode: (nodeId: TradeLogicNodeId) => void;
    onRemoveNode: (nodeId: TradeLogicNodeId) => void;
}

const basicGroupOperatorOptions: { value: LogicGroupOperator; label: string }[] = [
    { value: 'all', label: 'All conditions' },
    { value: 'any', label: 'Any condition' }
];

const advancedGroupOperatorOptions: { value: LogicGroupOperator; label: string }[] = [
    { value: 'not', label: 'Not' },
    { value: 'threshold', label: 'Threshold' },
    { value: 'weighted', label: 'Weighted' },
    { value: 'firstMatch', label: 'First match' }
];

const ADVANCED_GROUP_OPERATOR_KEYS: ReadonlySet<LogicGroupOperator> = new Set(advancedGroupOperatorOptions.map((option) => option.value));

const GROUP_OPERATORS_USING_THRESHOLD: ReadonlySet<LogicGroupOperator> = new Set(['threshold', 'weighted']);

const sequenceModeOptions: { value: LogicSequenceMode; label: string }[] = [
    { value: 'ordered', label: 'Ordered' },
    { value: 'firstMatch', label: 'First match' },
    { value: 'allActions', label: 'All actions' }
];

const actionKindOptions = [
    { value: 'enter', label: 'Enter' },
    { value: 'exit', label: 'Exit' },
    { value: 'partialExit', label: 'Partial exit' },
    { value: 'moveStop', label: 'Move stop' },
    { value: 'moveTarget', label: 'Move target' },
    { value: 'armOrder', label: 'Arm order' },
    { value: 'cancelOrder', label: 'Cancel order' },
    { value: 'adjustOrder', label: 'Adjust order' },
    { value: 'hold', label: 'Hold' },
    { value: 'noAction', label: 'No action' }
];

const directionOptions = [
    { value: '', label: 'Default' },
    { value: 'long', label: 'Long' },
    { value: 'short', label: 'Short' },
    { value: 'either', label: 'Either' }
];

const quantityExpressionModeOptions: { value: QuantityExpressionMode; label: string }[] = [
    { value: 'literal', label: 'Numeric literal' },
    { value: 'json', label: 'Advanced JSON' },
    { value: 'blank', label: 'Blank' }
];

const conditionOperatorOptions: { value: NonNullable<LogicConditionNode['operator']>; label: string }[] = [
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
    { value: '>=', label: '>=' },
    { value: '<=', label: '<=' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'not contains' },
    { value: 'any', label: 'any' },
    { value: 'all', label: 'all' },
    { value: 'empty', label: 'empty' },
    { value: 'not_empty', label: 'not empty' },
    { value: 'in', label: 'in' },
    { value: 'not_in', label: 'not in' },
    { value: 'between', label: 'between' },
    { value: 'outside', label: 'outside' }
];

const scheduleKindOptions: { value: LogicScheduleNode['scheduleKind']; label: string }[] = [
    { value: 'marketSession', label: 'Market session' },
    { value: 'timeOfDay', label: 'Time of day' },
    { value: 'dayOfWeek', label: 'Day of week' },
    { value: 'entryAge', label: 'Entry age' },
    { value: 'optionExpiry', label: 'Option expiry' },
    { value: 'custom', label: 'Custom' }
];

const nativeScheduleTypeOptions = [
    { value: 'fixedTimes', label: 'Fixed times' },
    { value: 'everyDuration', label: 'Every interval' },
    { value: 'marketOffset', label: 'Market offsets' },
    { value: 'json', label: 'Raw JSON' }
];

const scheduleCalendarOptions = [
    { value: 'market', label: 'Market' },
    { value: 'calendar', label: 'Calendar' }
];

const scheduleIntervalUnitOptions = [
    { value: 'seconds', label: 'Seconds' },
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' }
];

const scheduleMarketAnchorOptions = [
    { value: 'sessionOpen', label: 'Session open' },
    { value: 'marketOpen', label: 'Market open' },
    { value: 'marketClose', label: 'Market close' },
    { value: 'sessionClose', label: 'Session close' }
];

const missingDataPolicyOptions: { value: '' | TradeLogicMissingDataPolicy; label: string }[] = [
    { value: '', label: 'Default' },
    { value: 'fail', label: 'Fail' },
    { value: 'treatAsFalse', label: 'Treat as false' },
    { value: 'treatAsUnknown', label: 'Treat as unknown' },
    { value: 'carryForward', label: 'Carry forward' },
    { value: 'skipNode', label: 'Skip node' }
];

const trailingModeOptions: { value: '' | TradeLogicTrailingMode; label: string }[] = [
    { value: '', label: 'None' },
    { value: 'withinBars', label: 'Within bars' }
];

const NodeInspectorPanel = ({
    node,
    priceWaitAvailable,
    priceCaptureOptions,
    diagnostics,
    actionTemplatesByKey,
    conditionTemplatesByKey,
    definitionOptions,
    featuresByKey,
    featureRefValidationState = 'idle',
    onLabelChange,
    onGroupChange,
    onSequenceChange,
    onScheduleChange,
    onReferenceChange,
    onConditionChange,
    onActionChange,
    onToggleNode,
    onRemoveNode
}: NodeInspectorPanelProps) => {
    if (!node) {
        return (
            <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4">
                <h5 className="text-base font-semibold text-zinc-100">Node Inspector</h5>
                <p className="mt-2 text-sm text-zinc-500">Select a draft node to edit its local metadata.</p>
            </div>
        );
    }

    const nodeDiagnostics = diagnostics.filter((diagnostic) => diagnostic.nodeId === node.id);

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h5 className="text-base font-semibold text-zinc-100">Node Inspector</h5>
                    <p className="mt-1 text-xs text-zinc-500">{node.id}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge variant="dark">{node.kind}</Badge>
                    {!node.enabled && <Badge variant="warning">Disabled</Badge>}
                </div>
            </div>

            <div className="space-y-3">
                <FormInput label="Label" value={node.label ?? ''} onChange={(event) => onLabelChange(node.id, event.target.value)} />

                {node.kind === 'group' && <GroupNodeEditor key={getStructureEditorKey(node)} node={node} onGroupChange={onGroupChange} />}

                {node.kind === 'sequence' && (
                    <SequenceNodeEditor key={getStructureEditorKey(node)} node={node} onSequenceChange={onSequenceChange} />
                )}
                {node.kind === 'condition' && (
                    <>
                        {(() => {
                            const isImportedConditionNode = !node.templateKey && hasImportedConditionExpression(node);

                            return (
                                <DetailRow
                                    label="Template"
                                    value={isImportedConditionNode ? 'Imported raw condition' : (node.templateKey ?? 'Feature condition')}
                                />
                            );
                        })()}
                        <DetailRow label="Feature" value={node.featureRef?.featureKey ?? 'None'} />
                        <DetailRow label="Backend Feature Ref" value={formatFeatureRefValidationState(featureRefValidationState)} />
                        <ConditionNodeEditor
                            conditionTemplatesByKey={conditionTemplatesByKey}
                            featuresByKey={featuresByKey}
                            key={getConditionEditorKey(node, featuresByKey, conditionTemplatesByKey)}
                            node={node}
                            onConditionChange={onConditionChange}
                        />
                    </>
                )}
                {node.kind === 'action' && (
                    <>
                        <DetailRow label="Template" value={node.templateKey ?? 'None'} />
                        <DetailRow label="Action Kind" value={node.actionKind} />
                        <ActionNodeEditor
                            actionTemplatesByKey={actionTemplatesByKey}
                            key={getActionEditorKey(node, actionTemplatesByKey)}
                            node={node}
                            onActionChange={onActionChange}
                            priceWaitAvailable={priceWaitAvailable}
                            priceCaptureOptions={priceCaptureOptions}
                        />
                    </>
                )}
                {node.kind === 'schedule' && (
                    <ScheduleNodeEditor key={getStructureEditorKey(node)} node={node} onScheduleChange={onScheduleChange} />
                )}
                {node.kind === 'reference' && (
                    <>
                        <DetailRow label="Reference Kind" value={node.referenceKind} />
                        <DetailRow label="Reference ID" value={String(node.referenceId)} />
                        <ReferenceNodeEditor
                            definitionOptions={definitionOptions}
                            key={node.id}
                            node={node}
                            onReferenceChange={onReferenceChange}
                        />
                    </>
                )}
            </div>

            {nodeDiagnostics.length > 0 && (
                <div className="mt-4 space-y-2">
                    {nodeDiagnostics.map((diagnostic, index) => (
                        <div
                            className="rounded-md border border-red-900/60 bg-red-950/30 p-2 text-xs text-red-100"
                            key={`${diagnostic.id}:${index}`}
                        >
                            <span className="font-semibold">{diagnostic.severity.toUpperCase()}</span> {diagnostic.message}
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline-dark" onClick={() => onToggleNode(node.id)}>
                    {node.enabled ? 'Disable Node' : 'Enable Node'}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => onRemoveNode(node.id)}>
                    Remove Node
                </Button>
            </div>
        </div>
    );
};

const getStructureEditorKey = (node: LogicGroupNode | LogicScheduleNode | LogicSequenceNode) => node.id;

interface GroupNodeEditorProps {
    node: LogicGroupNode;
    onGroupChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicGroupNode>) => void;
}

const GroupNodeEditor = ({ node, onGroupChange }: GroupNodeEditorProps) => {
    const [operator, setOperator] = useState<LogicGroupOperator>(node.operator);
    const [threshold, setThreshold] = useState(numberToInputString(node.threshold));
    const [weightThreshold, setWeightThreshold] = useState(numberToInputString(node.weightThreshold));
    const [editError, setEditError] = useState<string | null>(null);
    const [showAdvancedOperators, setShowAdvancedOperators] = useState(ADVANCED_GROUP_OPERATOR_KEYS.has(node.operator));

    const operatorOptions = showAdvancedOperators
        ? [...basicGroupOperatorOptions, ...advancedGroupOperatorOptions]
        : basicGroupOperatorOptions;

    const showThresholdInputs = showAdvancedOperators || GROUP_OPERATORS_USING_THRESHOLD.has(operator);

    const applyChanges = () => {
        try {
            onGroupChange(node.id, {
                operator,
                threshold: parseOptionalNumber(threshold, 'Threshold'),
                weightThreshold: parseOptionalNumber(weightThreshold, 'Weight threshold')
            });

            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update group node');
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormSelect
                    label="Group Operator"
                    options={operatorOptions}
                    value={operator}
                    onChange={(event) => setOperator(event.target.value as LogicGroupOperator)}
                />
                {showThresholdInputs && (
                    <>
                        <FormNumberInput
                            helpText="Used by threshold group operators."
                            label="Threshold"
                            min={0}
                            value={parseNumericDraft(threshold)}
                            onValueChange={(numericValue) => setThreshold(toNumericDraft(numericValue))}
                        />
                        <FormNumberInput
                            helpText="Used by weighted group operators."
                            label="Weight Threshold"
                            min={0}
                            value={parseNumericDraft(weightThreshold)}
                            onValueChange={(numericValue) => setWeightThreshold(toNumericDraft(numericValue))}
                        />
                    </>
                )}
            </div>
            <div className="mt-3">
                <FormCheckbox
                    checked={showAdvancedOperators}
                    id={`group-operator-show-advanced-${node.id}`}
                    label="Show advanced operators (not, threshold, weighted, first match)"
                    labelClassName="text-zinc-300"
                    onChange={(event) => setShowAdvancedOperators(event.target.checked)}
                />
            </div>
            <EditorError message={editError} />
            <ApplyEditorButton label="Apply Group Edits" onClick={applyChanges} />
        </div>
    );
};

interface SequenceNodeEditorProps {
    node: LogicSequenceNode;
    onSequenceChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicSequenceNode>) => void;
}

const SequenceNodeEditor = ({ node, onSequenceChange }: SequenceNodeEditorProps) => {
    const steps = node.children.filter((child) => child.enabled && child.kind !== 'action' && child.kind !== 'schedule');
    return (
        <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
            <FormSelect
                label="Sequence mode"
                value={node.mode}
                options={sequenceModeOptions}
                onChange={(event) => {
                    const selected = sequenceModeOptions.find((option) => option.value === event.target.value);
                    if (selected) onSequenceChange(node.id, { mode: selected.value });
                }}
            />
            <p className="text-xs text-zinc-400">
                Each step needs a new observation. Put conditions that should match together inside one group. New starting events do not
                restart an active setup.
            </p>
            {steps.map((step, index) => (
                <div className="space-y-2" key={step.id + JSON.stringify(node.stepTiming[step.id] ?? null)}>
                    <SequenceStepTimingEditor
                        label={`Step ${index + 1}: ${step.label ?? step.id}`}
                        timing={node.stepTiming[step.id]}
                        first={index === 0}
                        onChange={(timing) => onSequenceChange(node.id, { stepTiming: { ...node.stepTiming, [step.id]: timing } })}
                    />
                    {(() => {
                        const sourceOptions = collectSequenceStepPriceCaptureSources(step);
                        const capture = node.stepCaptures?.[step.id];
                        const selected = capture ? findSequencePriceCaptureSource(sourceOptions, capture.sourceRef) : undefined;
                        return (
                            <div className="grid grid-cols-1 gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 p-3 md:grid-cols-2">
                                <FormSelect
                                    label="Capture completed price"
                                    options={[
                                        { value: '', label: 'Do not capture' },
                                        ...sourceOptions.map((option) => ({ value: option.id, label: option.label }))
                                    ]}
                                    value={selected?.id ?? ''}
                                    onChange={(event) => {
                                        const source = sourceOptions.find((option) => option.id === event.target.value);
                                        onSequenceChange(node.id, {
                                            stepCaptures: withSequenceStepCapture(
                                                node,
                                                step.id,
                                                source ? { key: capture?.key || `capturedPrice${index + 1}`, sourceRef: source.ref } : null
                                            )
                                        });
                                    }}
                                />
                                {capture && (
                                    <FormInput
                                        label="Capture name"
                                        value={capture.key}
                                        onChange={(event) =>
                                            onSequenceChange(node.id, {
                                                stepCaptures: withSequenceStepCapture(node, step.id, {
                                                    ...capture,
                                                    key: event.target.value
                                                })
                                            })
                                        }
                                    />
                                )}
                                {sourceOptions.length === 0 && (
                                    <p className="text-xs text-zinc-500 md:col-span-2">
                                        Add a completed OHLC, EMA, SMA, or intraday VWAP condition to this step to capture its value.
                                    </p>
                                )}
                            </div>
                        );
                    })()}
                </div>
            ))}
        </div>
    );
};

interface ScheduleNodeEditorProps {
    node: LogicScheduleNode;
    onScheduleChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicScheduleNode>) => void;
}

const ScheduleNodeEditor = ({ node, onScheduleChange }: ScheduleNodeEditorProps) => {
    const [scheduleKind, setScheduleKind] = useState<LogicScheduleNode['scheduleKind']>(node.scheduleKind);
    const [parameterState, setParameterState] = useState(() => buildScheduleParameterInputState(node.parameters));
    const [applyNotice, setApplyNotice] = useState<string | null>(null);
    const [editError, setEditError] = useState<string | null>(null);

    const applyChanges = () => {
        try {
            onScheduleChange(node.id, {
                parameters: parseScheduleParameterInputState(scheduleKind, parameterState),
                scheduleKind
            });

            setEditError(null);
            setApplyNotice('Schedule draft updated. Publish to apply it to the strategy.');
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update schedule node');
            setApplyNotice(null);
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <FormSelect
                label="Schedule Kind"
                options={scheduleKindOptions}
                value={scheduleKind}
                onChange={(event) => {
                    setScheduleKind(event.target.value as LogicScheduleNode['scheduleKind']);
                    setApplyNotice(null);
                }}
            />
            <ScheduleParameterFields
                parameterState={parameterState}
                scheduleKind={scheduleKind}
                onParameterStateChange={(state) => {
                    setParameterState(state);
                    setApplyNotice(null);
                }}
            />
            <EditorError message={editError} />
            <EditorNotice message={applyNotice} />
            <ApplyEditorButton label="Apply Schedule Edits" onClick={applyChanges} />
        </div>
    );
};

const ScheduleParameterFields = ({
    parameterState,
    scheduleKind,
    onParameterStateChange
}: {
    parameterState: ReturnType<typeof buildScheduleParameterInputState>;
    scheduleKind: LogicScheduleNode['scheduleKind'];
    onParameterStateChange: (state: ReturnType<typeof buildScheduleParameterInputState>) => void;
}) => {
    const updateParameterState = (changes: Partial<ReturnType<typeof buildScheduleParameterInputState>>) =>
        onParameterStateChange({
            ...parameterState,
            ...changes
        });

    switch (scheduleKind) {
        case 'marketSession':
            return (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormSelect
                        label="Session"
                        options={scheduleSessionOptions}
                        value={parameterState.session}
                        onChange={(event) => updateParameterState({ session: event.target.value })}
                    />
                    <FormInput
                        label="Timezone"
                        value={parameterState.timezone}
                        onChange={(event) => updateParameterState({ timezone: event.target.value })}
                    />
                </div>
            );
        case 'timeOfDay':
            return (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <FormInput
                        helpText="HH:MM"
                        label="Start Time"
                        placeholder="09:30"
                        value={parameterState.startTime}
                        onChange={(event) => updateParameterState({ startTime: event.target.value })}
                    />
                    <FormInput
                        helpText="HH:MM"
                        label="End Time"
                        placeholder="16:00"
                        value={parameterState.endTime}
                        onChange={(event) => updateParameterState({ endTime: event.target.value })}
                    />
                    <FormInput
                        label="Timezone"
                        value={parameterState.timezone}
                        onChange={(event) => updateParameterState({ timezone: event.target.value })}
                    />
                </div>
            );
        case 'dayOfWeek':
            return (
                <fieldset className="mt-3">
                    <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Allowed days</legend>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                        {scheduleDayOfWeekOptions.map((option) => (
                            <FormCheckbox
                                checked={parameterState.daysOfWeek.includes(option.value)}
                                id={`schedule-day-${option.value}`}
                                key={option.value}
                                label={option.label}
                                labelClassName="text-zinc-300"
                                onChange={(event) =>
                                    updateParameterState({
                                        daysOfWeek: toggleScheduleDayOfWeek(parameterState.daysOfWeek, option.value, event.target.checked)
                                    })
                                }
                            />
                        ))}
                    </div>
                </fieldset>
            );
        case 'entryAge':
            return (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormNumberInput
                        integer
                        label="Minimum Bars"
                        min={0}
                        value={parseNumericDraft(parameterState.minBars)}
                        onValueChange={(numericValue) => updateParameterState({ minBars: toNumericDraft(numericValue) })}
                    />
                    <FormNumberInput
                        integer
                        label="Maximum Bars"
                        min={0}
                        value={parseNumericDraft(parameterState.maxBars)}
                        onValueChange={(numericValue) => updateParameterState({ maxBars: toNumericDraft(numericValue) })}
                    />
                    <FormNumberInput
                        integer
                        label="Minimum Minutes"
                        min={0}
                        value={parseNumericDraft(parameterState.minMinutes)}
                        onValueChange={(numericValue) => updateParameterState({ minMinutes: toNumericDraft(numericValue) })}
                    />
                    <FormNumberInput
                        integer
                        label="Maximum Minutes"
                        min={0}
                        value={parseNumericDraft(parameterState.maxMinutes)}
                        onValueChange={(numericValue) => updateParameterState({ maxMinutes: toNumericDraft(numericValue) })}
                    />
                </div>
            );
        case 'optionExpiry':
            return (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormNumberInput
                        integer
                        label="Minimum Days To Expiry"
                        min={0}
                        value={parseNumericDraft(parameterState.minDaysToExpiry)}
                        onValueChange={(numericValue) => updateParameterState({ minDaysToExpiry: toNumericDraft(numericValue) })}
                    />
                    <FormNumberInput
                        integer
                        label="Maximum Days To Expiry"
                        min={0}
                        value={parseNumericDraft(parameterState.maxDaysToExpiry)}
                        onValueChange={(numericValue) => updateParameterState({ maxDaysToExpiry: toNumericDraft(numericValue) })}
                    />
                </div>
            );
        case 'custom':
        default:
            return (
                <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <FormSelect
                            label="Native Schedule"
                            options={nativeScheduleTypeOptions}
                            value={parameterState.scheduleType}
                            onChange={(event) => updateParameterState({ scheduleType: event.target.value })}
                        />
                        <FormSelect
                            label="Calendar"
                            options={scheduleCalendarOptions}
                            value={parameterState.calendar}
                            onChange={(event) => updateParameterState({ calendar: event.target.value })}
                        />
                        <FormInput
                            label="Timezone"
                            value={parameterState.timezone}
                            onChange={(event) => updateParameterState({ timezone: event.target.value })}
                        />
                    </div>
                    <fieldset>
                        <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Allowed days</legend>
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                            {scheduleDayOfWeekOptions.map((option) => (
                                <FormCheckbox
                                    checked={parameterState.daysOfWeek.includes(option.value)}
                                    id={`custom-schedule-day-${option.value}`}
                                    key={option.value}
                                    label={option.label}
                                    labelClassName="text-zinc-300"
                                    onChange={(event) =>
                                        updateParameterState({
                                            daysOfWeek: toggleScheduleDayOfWeek(
                                                parameterState.daysOfWeek,
                                                option.value,
                                                event.target.checked
                                            )
                                        })
                                    }
                                />
                            ))}
                        </div>
                    </fieldset>
                    {parameterState.scheduleType === 'fixedTimes' && (
                        <FormInput
                            helpText="Comma-separated HH:MM values."
                            label="Fixed Times"
                            placeholder="09:35, 15:55"
                            value={parameterState.times}
                            onChange={(event) => updateParameterState({ times: event.target.value })}
                        />
                    )}
                    {parameterState.scheduleType === 'everyDuration' && (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                            <FormNumberInput
                                integer
                                label="Interval"
                                min={1}
                                value={parseNumericDraft(parameterState.intervalValue)}
                                onValueChange={(numericValue) => updateParameterState({ intervalValue: toNumericDraft(numericValue) })}
                            />
                            <FormSelect
                                label="Unit"
                                options={scheduleIntervalUnitOptions}
                                value={parameterState.intervalUnit}
                                onChange={(event) => updateParameterState({ intervalUnit: event.target.value })}
                            />
                            <FormSelect
                                label="Start Anchor"
                                options={scheduleMarketAnchorOptions}
                                value={parameterState.startAnchor}
                                onChange={(event) => updateParameterState({ startAnchor: event.target.value })}
                            />
                            <FormSelect
                                label="End Anchor"
                                options={scheduleMarketAnchorOptions}
                                value={parameterState.endAnchor}
                                onChange={(event) => updateParameterState({ endAnchor: event.target.value })}
                            />
                        </div>
                    )}
                    {parameterState.scheduleType === 'marketOffset' && (
                        <>
                            <FormSelect
                                label="Anchor"
                                options={scheduleMarketAnchorOptions}
                                value={parameterState.anchor}
                                onChange={(event) => updateParameterState({ anchor: event.target.value })}
                            />
                            <JsonObjectTextArea
                                label="Offsets JSON"
                                value={parameterState.offsetsJson}
                                onChange={(offsetsJson) => updateParameterState({ offsetsJson })}
                            />
                        </>
                    )}
                    {parameterState.scheduleType === 'json' && (
                        <JsonObjectTextArea
                            label="Parameters JSON"
                            value={parameterState.customParametersJson}
                            onChange={(customParametersJson) => updateParameterState({ customParametersJson })}
                        />
                    )}
                </div>
            );
    }
};

interface ReferenceNodeEditorProps {
    definitionOptions: SelectOption[];
    node: LogicReferenceNode;
    onReferenceChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicReferenceNode>) => void;
}

const ReferenceNodeEditor = ({ definitionOptions, node, onReferenceChange }: ReferenceNodeEditorProps) => {
    const initialState = useMemo(() => buildReferenceNodeInputState(node), [node]);
    const [referenceKind, setReferenceKind] = useState<LogicReferenceKind>(() => initialState.referenceKind);
    const [referenceId, setReferenceId] = useState(() => initialState.referenceId);
    const [version, setVersion] = useState(() => initialState.version);
    const [parametersJson, setParametersJson] = useState(() => initialState.parametersJson);
    const [editError, setEditError] = useState<string | null>(null);

    const visibleDefinitionOptions = useMemo(() => {
        if (referenceKind !== 'definition' || !referenceId || definitionOptions.some((option) => option.value === referenceId)) {
            return definitionOptions;
        }

        return [{ value: referenceId, label: `${referenceId} - unavailable to this user`, disabled: true }, ...definitionOptions];
    }, [definitionOptions, referenceId, referenceKind]);

    const applyChanges = () => {
        try {
            onReferenceChange(
                node.id,
                parseReferenceNodeInputState({
                    parametersJson,
                    referenceId,
                    referenceKind,
                    version
                })
            );

            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update reference node');
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormSelect
                    label="Reference Kind"
                    options={referenceKindOptions}
                    value={referenceKind}
                    onChange={(event) => setReferenceKind(event.target.value as LogicReferenceKind)}
                />
                {referenceKind === 'definition' ? (
                    <FormSelect
                        label="Definition"
                        options={visibleDefinitionOptions}
                        value={referenceId}
                        onChange={(event) => setReferenceId(event.target.value)}
                    />
                ) : (
                    <FormInput label="Reference ID" value={referenceId} onChange={(event) => setReferenceId(event.target.value)} />
                )}
                <FormNumberInput
                    integer
                    helpText="Optional positive integer."
                    label="Version"
                    min={1}
                    value={parseNumericDraft(version)}
                    onValueChange={(numericValue) => setVersion(toNumericDraft(numericValue))}
                />
            </div>
            <JsonObjectTextArea label="Parameters JSON" value={parametersJson} onChange={setParametersJson} />
            <EditorError message={editError} />
            <ApplyEditorButton label="Apply Reference Edits" onClick={applyChanges} />
        </div>
    );
};

const getConditionEditorKey = (
    node: LogicConditionNode,
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>,
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>
) => {
    const feature = node.featureRef?.featureKey ? featuresByKey[node.featureRef.featureKey] : null;
    const template = node.templateKey ? conditionTemplatesByKey[node.templateKey] : null;

    return [
        node.id,
        feature?.semanticFingerprint ?? feature?.featureKey ?? '',
        template?.semanticFingerprint ?? template?.templateKey ?? ''
    ].join(':');
};

const getActionEditorKey = (node: LogicActionNode, actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>) => {
    const template = node.templateKey ? actionTemplatesByKey[node.templateKey] : null;

    return [node.id, template?.semanticFingerprint ?? template?.templateKey ?? ''].join(':');
};

interface ConditionNodeEditorProps {
    node: LogicConditionNode;
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
    onConditionChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicConditionNode>) => void;
}

export const ConditionNodeEditor = ({ node, featuresByKey, conditionTemplatesByKey, onConditionChange }: ConditionNodeEditorProps) => {
    const isImportedConditionNode = !node.templateKey && hasImportedConditionExpression(node);

    const feature = node.featureRef?.featureKey ? (featuresByKey[node.featureRef.featureKey] ?? null) : null;
    const template = node.templateKey ? (conditionTemplatesByKey[node.templateKey] ?? null) : null;
    const valueField = useMemo(() => buildConditionTemplateValueField(template), [template]);

    const operatorOptions = useMemo<string[]>(
        () => (template?.allowedOperators.length ? template.allowedOperators : feature?.allowedOperators) ?? ['='],
        [feature, template]
    );

    const initialState = useMemo(
        () => buildConditionEditorInitialState({ feature, node, operatorOptions, template, valueField }),
        [feature, node, operatorOptions, template, valueField]
    );

    const [operator, setOperator] = useState<string>(() => initialState.operator);
    const [conditionValue, setConditionValue] = useState(() => initialState.conditionValue);
    const [duration, setDuration] = useState(() => initialState.duration);
    const [selectorValues, setSelectorValues] = useState<Record<string, string>>(() => initialState.selectorValues);
    const [filterValues, setFilterValues] = useState<Record<string, string>>(() => initialState.filterValues);

    const [filterOperators, setFilterOperators] = useState<Record<string, TradeLogicComparisonOperatorV2>>(
        () => initialState.filterOperators
    );

    const [parameterValues, setParameterValues] = useState<Record<string, string>>(() => initialState.parameterValues);
    const [missingDataPolicy, setMissingDataPolicy] = useState<'' | TradeLogicMissingDataPolicy>(() => initialState.missingDataPolicy);
    const [trailingState, setTrailingState] = useState<TrailingInputState>(() => initialState.trailingState);
    const [editError, setEditError] = useState<string | null>(null);

    // Hooks above must run unconditionally; structural early returns live here, after them.
    if (isImportedConditionNode) {
        return <ImportedConditionNodeEditor node={node} />;
    }

    if (!feature && !template) {
        return <TemplateLessConditionNodeEditor node={node} onConditionChange={onConditionChange} />;
    }

    const applyChanges = () => {
        try {
            const changes = buildConditionNodePatch({
                conditionValue,
                duration,
                feature,
                filterOperators,
                filterValues,
                missingDataPolicy,
                node,
                operator,
                parameterValues,
                selectorValues,
                template,
                trailingState,
                valueField
            });

            onConditionChange(node.id, changes);
            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update condition node');
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormSelect
                    label="Operator"
                    options={operatorOptions.map((option) => ({ value: option, label: option }))}
                    value={operator}
                    onChange={(event) => setOperator(event.target.value)}
                />
                {feature && (
                    <FeatureConditionValueInput feature={feature} operator={operator} value={conditionValue} onChange={setConditionValue} />
                )}
                {!feature && valueField && <SchemaFieldInput field={valueField} value={conditionValue} onChange={setConditionValue} />}
                {feature && node.featureRef && (feature.defaultDuration != null || node.featureRef.duration != null) && (
                    <FormSelect
                        label="Feature Duration"
                        helpText="Bar duration used to calculate this feature, independent of the strategy's duration."
                        value={duration}
                        options={[
                            { value: '', label: 'No explicit duration' },
                            ...STANDARD_DURATIONS.map((value) => ({ value, label: value })),
                            ...(duration && !STANDARD_DURATIONS.some((value) => value === duration)
                                ? [{ value: duration, label: `${duration} (unsupported)`, disabled: true }]
                                : [])
                        ]}
                        onChange={(event) => setDuration(event.target.value)}
                    />
                )}
                <FormSelect
                    label="Missing Data"
                    options={missingDataPolicyOptions}
                    value={missingDataPolicy}
                    onChange={(event) => setMissingDataPolicy(event.target.value as '' | TradeLogicMissingDataPolicy)}
                />
                {feature && node.featureRef && (
                    <>
                        <FormSelect
                            helpText="Match against trailing feature history instead of only the current bar."
                            label="Trailing Mode"
                            options={trailingModeOptions}
                            value={trailingState.trailingMode}
                            onChange={(event) =>
                                setTrailingState({
                                    ...trailingState,
                                    trailingMode: event.target.value as '' | TradeLogicTrailingMode
                                })
                            }
                        />
                        <FormNumberInput
                            integer
                            helpText="Optional positive integer. Requires a trailing mode."
                            label="Within Bars"
                            min={1}
                            value={parseNumericDraft(trailingState.withinBars)}
                            onValueChange={(numericValue) =>
                                setTrailingState({ ...trailingState, withinBars: toNumericDraft(numericValue) })
                            }
                        />
                    </>
                )}
            </div>
            {feature && feature.selectorFields.length > 0 && (
                <SchemaFieldsEditor
                    fields={feature.selectorFields}
                    title="Selectors"
                    values={selectorValues}
                    onValuesChange={setSelectorValues}
                />
            )}
            {feature && feature.filterFields.length > 0 && (
                <FilterFieldsEditor
                    fields={feature.filterFields}
                    operators={filterOperators}
                    values={filterValues}
                    onOperatorsChange={setFilterOperators}
                    onValuesChange={setFilterValues}
                />
            )}
            {template && template.parameterFields.length > 0 && (
                <SchemaFieldsEditor
                    fields={template.parameterFields}
                    title="Template Parameters"
                    values={parameterValues}
                    onValuesChange={setParameterValues}
                />
            )}
            {editError && (
                <div className="mt-3 rounded-md border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-100">{editError}</div>
            )}
            <div className="mt-3">
                <Button size="sm" variant="secondary" onClick={applyChanges}>
                    Apply Node Edits
                </Button>
            </div>
        </div>
    );
};

const ImportedConditionNodeEditor = ({ node }: { node: LogicConditionNode }) => (
    <div className="rounded-md border border-amber-900/60 bg-amber-950/20 p-3">
        <div className="text-sm text-amber-100">
            This condition was imported from a published Trade Logic V2 definition and is backed by a preserved raw expression.
        </div>
        <div className="mt-2 text-xs text-amber-200/80">
            Rename it with the Label field above. Feature, operator, and raw JSON edits are disabled here to avoid corrupting the imported
            condition.
        </div>
        <ReadOnlyJsonTextArea
            label="Imported Expression JSON"
            value={jsonValueToTextAreaString(node.metadata?.importedConditionExpression ?? node.parameters?.importedConditionExpression)}
        />
    </div>
);

interface TemplateLessConditionNodeEditorProps {
    node: LogicConditionNode;
    onConditionChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicConditionNode>) => void;
}

const TemplateLessConditionNodeEditor = ({ node, onConditionChange }: TemplateLessConditionNodeEditorProps) => {
    const [operator, setOperator] = useState<NonNullable<LogicConditionNode['operator']>>(node.operator ?? '=');
    const [featureRefJson, setFeatureRefJson] = useState(() => jsonValueToTextAreaString(node.featureRef ?? undefined));
    const [valueJson, setValueJson] = useState(() => jsonValueToTextAreaString(node.value));
    const [parametersJson, setParametersJson] = useState(() => jsonValueToTextAreaString(node.parameters ?? undefined));
    const [missingDataPolicy, setMissingDataPolicy] = useState<'' | TradeLogicMissingDataPolicy>(node.missingDataPolicy ?? '');
    const [editError, setEditError] = useState<string | null>(null);

    const applyChanges = () => {
        try {
            const featureRef = parseOptionalContextFeatureRef(featureRefJson);
            const parameters = parseOptionalJsonObject(parametersJson, 'Parameters JSON');

            onConditionChange(node.id, {
                featureRef,
                missingDataPolicy: missingDataPolicy || null,
                operator,
                parameters: parameters ?? undefined,
                value: parseOptionalJsonValue(valueJson, 'Value JSON') ?? null
            });

            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update condition node');
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormSelect
                    label="Operator"
                    options={conditionOperatorOptions}
                    value={operator}
                    onChange={(event) => setOperator(event.target.value as NonNullable<LogicConditionNode['operator']>)}
                />
                <FormSelect
                    label="Missing Data"
                    options={missingDataPolicyOptions}
                    value={missingDataPolicy}
                    onChange={(event) => setMissingDataPolicy(event.target.value as '' | TradeLogicMissingDataPolicy)}
                />
            </div>
            <JsonObjectTextArea label="Feature Ref JSON" value={featureRefJson} onChange={setFeatureRefJson} />
            <JsonObjectTextArea label="Value JSON" value={valueJson} onChange={setValueJson} />
            <JsonObjectTextArea label="Parameters JSON" value={parametersJson} onChange={setParametersJson} />
            <EditorError message={editError} />
            <ApplyEditorButton label="Apply Condition Edits" onClick={applyChanges} />
        </div>
    );
};

interface ConditionEditorInitialStateInput {
    node: LogicConditionNode;
    feature: TradeLogicRegistryFeatureDto | null;
    template: TradeLogicRegistryConditionTemplateDto | null;
    valueField: TradeLogicRegistrySchemaFieldDto | null;
    operatorOptions: string[];
}

interface ConditionEditorInitialState {
    conditionValue: string;
    duration: string;
    filterOperators: Record<string, TradeLogicComparisonOperatorV2>;
    filterValues: Record<string, string>;
    missingDataPolicy: '' | TradeLogicMissingDataPolicy;
    operator: string;
    parameterValues: Record<string, string>;
    selectorValues: Record<string, string>;
    trailingState: TrailingInputState;
}

export const buildConditionEditorInitialState = ({
    node,
    feature,
    template,
    valueField,
    operatorOptions
}: ConditionEditorInitialStateInput): ConditionEditorInitialState => {
    const operator = getInitialConditionOperator(node, operatorOptions);
    const filterState = getInitialFilterInputState(node, feature);

    return {
        conditionValue: buildConditionValueInput({ feature, node, operator, valueField }),
        duration: node.featureRef?.duration ?? '',
        filterOperators: filterState.operators,
        filterValues: filterState.values,
        missingDataPolicy: node.missingDataPolicy ?? '',
        operator,
        parameterValues: getInitialParameterValues(node, template),
        selectorValues: getInitialSelectorValues(node, feature),
        trailingState: buildTrailingInputState(node.featureRef)
    };
};

const getInitialConditionOperator = resolveInitialConditionOperator;

const getInitialFilterInputState = (node: LogicConditionNode, feature: TradeLogicRegistryFeatureDto | null) => {
    if (!feature) {
        return {
            operators: {},
            values: {}
        };
    }

    return buildFilterInputState(feature.filterFields, node.featureRef?.filters);
};

const getInitialParameterValues = (
    node: LogicConditionNode,
    template: TradeLogicRegistryConditionTemplateDto | null
): Record<string, string> => {
    if (!template) return {};

    return buildSchemaInputValues(template.parameterFields, node.parameters);
};

const getInitialSelectorValues = (node: LogicConditionNode, feature: TradeLogicRegistryFeatureDto | null): Record<string, string> => {
    if (!feature) return {};

    return buildSchemaInputValues(feature.selectorFields, node.featureRef?.selectors);
};

interface BuildConditionNodePatchInput {
    node: LogicConditionNode;
    feature: TradeLogicRegistryFeatureDto | null;
    template: TradeLogicRegistryConditionTemplateDto | null;
    valueField: TradeLogicRegistrySchemaFieldDto | null;
    operator: string;
    conditionValue: string;
    duration: string;
    selectorValues: Record<string, string>;
    filterValues: Record<string, string>;
    filterOperators: Record<string, TradeLogicComparisonOperatorV2>;
    parameterValues: Record<string, string>;
    missingDataPolicy: '' | TradeLogicMissingDataPolicy;
    trailingState: TrailingInputState;
}

export const buildConditionNodePatch = ({
    node,
    feature,
    template,
    valueField,
    operator,
    conditionValue,
    duration,
    selectorValues,
    filterValues,
    filterOperators,
    parameterValues,
    missingDataPolicy,
    trailingState
}: BuildConditionNodePatchInput): Partial<LogicConditionNode> => {
    const patch: Partial<LogicConditionNode> = {
        missingDataPolicy: missingDataPolicy || null,
        operator: operator as LogicConditionNode['operator']
    };

    if (feature && node.featureRef) {
        if (duration && !STANDARD_DURATIONS.some((value) => value === duration)) {
            throw new Error(`Unsupported feature duration: ${duration}. Select a standard bar duration.`);
        }
        const selectors = buildParsedSchemaValues(feature.selectorFields, selectorValues);
        const filters = buildFeatureFilters(feature.filterFields, filterValues, filterOperators);
        const trailing = parseTrailingInputState(trailingState);

        patch.featureRef = {
            ...node.featureRef,
            duration: duration || null,
            selectors,
            filters: filters.length > 0 ? filters : null,
            trailingMode: trailing.trailingMode,
            withinBars: trailing.withinBars,
            missingDataPolicy: missingDataPolicy || null
        };

        patch.value = parseConditionValue(feature, operator, conditionValue);
    } else if (valueField) {
        patch.value = parseSchemaInputValue(conditionValue, valueField);
    }

    if (template) {
        patch.parameters = buildParsedSchemaValues(template.parameterFields, parameterValues);
    }

    return patch;
};

const buildConditionValueInput = ({
    feature,
    node,
    operator,
    valueField
}: {
    feature: TradeLogicRegistryFeatureDto | null;
    node: LogicConditionNode;
    operator: string;
    valueField: TradeLogicRegistrySchemaFieldDto | null;
}) => {
    if (feature) {
        return conditionValueToInputString(feature, operator, node.value);
    }

    if (valueField) {
        return schemaFieldValueToInputString(valueField, node.value);
    }

    return schemaValueToInputString(node.value);
};

interface FilterFieldsEditorProps {
    fields: TradeLogicRegistrySchemaFieldDto[];
    values: Record<string, string>;
    operators: Record<string, TradeLogicComparisonOperatorV2>;
    onValuesChange: (values: Record<string, string>) => void;
    onOperatorsChange: (operators: Record<string, TradeLogicComparisonOperatorV2>) => void;
}

const FilterFieldsEditor = ({ fields, values, operators, onValuesChange, onOperatorsChange }: FilterFieldsEditorProps) => (
    <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Feature Filters</div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {fields.map((field) => (
                <div className="grid grid-cols-[minmax(0,1fr)_112px] gap-2" key={field.key}>
                    <SchemaFieldInput
                        field={field}
                        value={values[field.key] ?? ''}
                        onChange={(value) => onValuesChange({ ...values, [field.key]: value })}
                    />
                    <FormSelect
                        label="Operator"
                        options={(field.operators.length > 0 ? field.operators : ['=']).map((option) => ({ value: option, label: option }))}
                        value={operators[field.key] ?? field.operators[0] ?? '='}
                        onChange={(event) =>
                            onOperatorsChange({
                                ...operators,
                                [field.key]: event.target.value as TradeLogicComparisonOperatorV2
                            })
                        }
                    />
                </div>
            ))}
        </div>
    </div>
);

interface ActionNodeEditorProps {
    node: LogicActionNode;
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
    onActionChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicActionNode>) => void;
    priceWaitAvailable: boolean;
    priceCaptureOptions: SequencePriceCaptureTargetOption[];
}

const ActionNodeEditor = ({
    node,
    actionTemplatesByKey,
    onActionChange,
    priceWaitAvailable,
    priceCaptureOptions
}: ActionNodeEditorProps) => {
    const template = node.templateKey ? (actionTemplatesByKey[node.templateKey] ?? null) : null;

    const isExit = node.actionKind === 'exit' || node.actionKind === 'partialExit';
    const initialParameterValues = useMemo(
        () =>
            template
                ? buildSchemaInputValues(template.parameterFields, {
                      ...node.parameters,
                      ...(isExit && !isAutomatedExitReason(node.parameters.reason) ? { reason: 'automatedExit' } : {})
                  })
                : {},
        [node.parameters, template, isExit]
    );

    const [parameterValues, setParameterValues] = useState<Record<string, string>>(() => initialParameterValues);
    const [description, setDescription] = useState(
        (node.actionKind === 'exit' || node.actionKind === 'partialExit'
            ? authoredExitDescription(node.parameters.reason, node.description)
            : node.description) ?? ''
    );
    const [editError, setEditError] = useState<string | null>(null);

    if (!template) {
        return (
            <TemplateLessActionNodeEditor
                node={node}
                onActionChange={onActionChange}
                priceWaitAvailable={priceWaitAvailable}
                priceCaptureOptions={priceCaptureOptions}
            />
        );
    }

    const applyChanges = () => {
        try {
            const parsedParameters = buildParsedSchemaValues(template.parameterFields, parameterValues);
            onActionChange(node.id, {
                description: description.trim(),
                parameters:
                    node.parameters.priceExecution != null
                        ? withPriceExecution(parsedParameters, node.parameters.priceExecution)
                        : parsedParameters
            });

            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update action node');
        }
    };

    const canApplyEntryDefaults = template.actionKind === 'enter' && hasEntryActionTemplateDefaults(template.parameterFields);

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            {canApplyEntryDefaults && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
                    <div>
                        <div className="text-sm font-semibold text-zinc-100">Entry action defaults</div>
                        <div className="mt-1 text-xs text-zinc-500">
                            Fill blank priority and size-fraction fields for standard strategy sizing.
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => setParameterValues(buildEntryActionTemplateDefaultValues(template.parameterFields, parameterValues))}
                    >
                        Use Entry Defaults
                    </Button>
                </div>
            )}
            <FormInput
                label="Rule explanation"
                value={description}
                maxLength={1000}
                onChange={(event) => setDescription(event.target.value)}
            />
            <SchemaFieldsEditor
                fields={template.parameterFields}
                title="Action Parameters"
                values={parameterValues}
                onValuesChange={setParameterValues}
            />
            {priceWaitAvailable && template.actionKind === 'enter' && node.actionKind === 'enter' && (
                <PriceExecutionEditor
                    captureOptions={priceCaptureOptions}
                    value={node.parameters.priceExecution}
                    onApply={(execution) => onActionChange(node.id, { parameters: withPriceExecution(node.parameters, execution) })}
                />
            )}
            {editError && (
                <div className="mt-3 rounded-md border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-100">{editError}</div>
            )}
            <div className="mt-3">
                <Button size="sm" variant="secondary" onClick={applyChanges}>
                    Apply Action Edits
                </Button>
            </div>
        </div>
    );
};

interface TemplateLessActionNodeEditorProps {
    node: LogicActionNode;
    onActionChange: (nodeId: TradeLogicNodeId, changes: Partial<LogicActionNode>) => void;
    priceWaitAvailable: boolean;
    priceCaptureOptions: SequencePriceCaptureTargetOption[];
}

const TemplateLessActionNodeEditor = ({
    node,
    onActionChange,
    priceWaitAvailable,
    priceCaptureOptions
}: TemplateLessActionNodeEditorProps) => {
    const [description, setDescription] = useState(
        (node.actionKind === 'exit' || node.actionKind === 'partialExit'
            ? authoredExitDescription(node.parameters.reason, node.description)
            : node.description) ?? ''
    );
    const [actionKind, setActionKind] = useState(node.actionKind);
    const [direction, setDirection] = useState(optionalStringParameterToInputString(node.parameters.direction));
    const [reason, setReason] = useState(
        (node.actionKind === 'exit' || node.actionKind === 'partialExit') && !isAutomatedExitReason(node.parameters.reason)
            ? 'automatedExit'
            : optionalStringParameterToInputString(node.parameters.reason)
    );

    const [priority, setPriority] = useState(
        numberToInputString(typeof node.parameters.priority === 'number' ? node.parameters.priority : null)
    );

    const [reduceOnly, setReduceOnly] = useState(Boolean(node.parameters.reduceOnly));
    const [quantityState, setQuantityState] = useState(() => buildQuantityExpressionInputState(node.parameters.quantity));
    const [priceJson, setPriceJson] = useState(() => jsonValueToTextAreaString(node.parameters.price));
    const [limitPriceJson, setLimitPriceJson] = useState(() => jsonValueToTextAreaString(node.parameters.limitPrice));
    const [stopPriceJson, setStopPriceJson] = useState(() => jsonValueToTextAreaString(node.parameters.stopPrice));
    const [parametersJson, setParametersJson] = useState(() => JSON.stringify(node.parameters, null, 2));
    const [editMode, setEditMode] = useState<'structured' | 'raw'>('structured');
    const [editError, setEditError] = useState<string | null>(null);

    const applyStructuredChanges = () => {
        try {
            const nextParameters: TradeLogicJsonObject = { ...node.parameters };
            const parsedPriority = parseOptionalNumber(priority, 'Priority');
            const quantity = parseQuantityExpressionInputState(quantityState);
            const price = parseOptionalJsonValue(priceJson, 'Price expression');
            const limitPrice = parseOptionalJsonValue(limitPriceJson, 'Limit price expression');
            const stopPrice = parseOptionalJsonValue(stopPriceJson, 'Stop price expression');

            if (direction) {
                nextParameters.direction = direction;
            } else {
                delete nextParameters.direction;
            }

            if (reason.trim()) {
                nextParameters.reason = reason.trim();
            } else {
                delete nextParameters.reason;
            }

            if (parsedPriority !== null) {
                nextParameters.priority = parsedPriority;
            } else {
                delete nextParameters.priority;
            }

            if (reduceOnly) {
                nextParameters.reduceOnly = true;
            } else {
                delete nextParameters.reduceOnly;
            }

            const expressionUpdates = {
                price,
                limitPrice,
                stopPrice
            };

            if (quantity === undefined) {
                delete nextParameters.quantity;
            } else {
                nextParameters.quantity = quantity;
            }

            for (const [key, value] of Object.entries(expressionUpdates)) {
                if (value === undefined) {
                    delete nextParameters[key];
                } else {
                    nextParameters[key] = value;
                }
            }

            onActionChange(node.id, {
                actionKind,
                description: description.trim(),
                parameters: nextParameters
            });

            setParametersJson(JSON.stringify(nextParameters, null, 2));
            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update action node');
        }
    };

    const applyRawChanges = () => {
        try {
            const parsedParameters = JSON.parse(parametersJson) as TradeLogicJsonValue;

            if (typeof parsedParameters !== 'object' || parsedParameters === null || Array.isArray(parsedParameters)) {
                throw new Error('Parameters JSON must be an object.');
            }

            onActionChange(node.id, {
                actionKind,
                description: description.trim(),
                parameters: parsedParameters
            });

            setEditError(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : 'Failed to update action node');
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="mb-3 flex flex-wrap gap-2">
                <Button
                    size="sm"
                    variant={editMode === 'structured' ? 'primary' : 'outline-dark'}
                    onClick={() => setEditMode('structured')}
                >
                    Structured
                </Button>
                <Button size="sm" variant={editMode === 'raw' ? 'primary' : 'outline-dark'} onClick={() => setEditMode('raw')}>
                    Raw JSON
                </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormSelect
                    label="Action Kind"
                    options={
                        actionKindOptions.some((option) => option.value === actionKind)
                            ? actionKindOptions
                            : [{ value: actionKind, label: actionKind }, ...actionKindOptions]
                    }
                    value={actionKind}
                    onChange={(event) => setActionKind(event.target.value)}
                />
                {editMode === 'structured' && (
                    <>
                        <FormSelect
                            label="Position side"
                            options={directionOptions}
                            value={direction}
                            onChange={(event) => setDirection(event.target.value)}
                        />
                        {actionKind === 'exit' || actionKind === 'partialExit' ? (
                            <FormSelect
                                label="Exit category"
                                value={reason || 'automatedExit'}
                                options={Object.entries(EXIT_REASON_LABELS)
                                    .filter(([key]) => isAutomatedExitReason(key))
                                    .map(([value, label]) => ({ value, label }))}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        ) : (
                            <FormInput label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />
                        )}
                        <FormInput
                            label="Rule explanation"
                            value={description}
                            maxLength={1000}
                            onChange={(event) => setDescription(event.target.value)}
                        />
                        <FormNumberInput
                            integer
                            label="Priority"
                            value={parseNumericDraft(priority)}
                            onValueChange={(numericValue) => setPriority(toNumericDraft(numericValue))}
                        />
                        <FormCheckbox
                            checked={reduceOnly}
                            label="Reduce only"
                            labelClassName="text-zinc-300"
                            onChange={(event) => setReduceOnly(event.target.checked)}
                        />
                    </>
                )}
            </div>
            {editMode === 'structured' ? (
                <>
                    <QuantityExpressionEditor
                        state={quantityState}
                        onChange={setQuantityState}
                        onUseFullSize={() =>
                            setQuantityState({
                                jsonValue: JSON.stringify(createLiteralQuantityExpression(1), null, 2),
                                literalValue: '1',
                                mode: 'literal'
                            })
                        }
                    />
                    <JsonObjectTextArea label="Price Expression JSON" value={priceJson} onChange={setPriceJson} />
                    <JsonObjectTextArea label="Limit Price Expression JSON" value={limitPriceJson} onChange={setLimitPriceJson} />
                    <JsonObjectTextArea label="Stop Price Expression JSON" value={stopPriceJson} onChange={setStopPriceJson} />
                    {priceWaitAvailable && actionKind === 'enter' && (
                        <PriceExecutionEditor
                            captureOptions={priceCaptureOptions}
                            value={node.parameters.priceExecution}
                            onApply={(execution) => {
                                const nextParameters = withPriceExecution(node.parameters, execution);
                                onActionChange(node.id, { actionKind, parameters: nextParameters });
                                setParametersJson(JSON.stringify(nextParameters, null, 2));
                                if (execution) {
                                    setPriceJson('');
                                    setLimitPriceJson('');
                                    setStopPriceJson('');
                                }
                            }}
                        />
                    )}
                    <EditorError message={editError} />
                    <ApplyEditorButton label="Apply Action Edits" onClick={applyStructuredChanges} />
                </>
            ) : (
                <>
                    <JsonObjectTextArea label="Parameters JSON" value={parametersJson} onChange={setParametersJson} />
                    <EditorError message={editError} />
                    <ApplyEditorButton label="Apply Raw Action Edits" onClick={applyRawChanges} />
                </>
            )}
        </div>
    );
};

interface QuantityExpressionEditorProps {
    state: QuantityExpressionInputState;
    onChange: (state: QuantityExpressionInputState) => void;
    onUseFullSize: () => void;
}

export const QuantityExpressionEditor = ({ state, onChange, onUseFullSize }: QuantityExpressionEditorProps) => (
    <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <FormSelect
                label="Size Mode"
                options={quantityExpressionModeOptions}
                value={state.mode}
                onChange={(event) =>
                    onChange({
                        ...state,
                        mode: event.target.value as QuantityExpressionMode
                    })
                }
            />
            {state.mode === 'literal' ? (
                <FormNumberInput
                    helpText="Use 1 for full size. Values up to 1 are fractions; larger values are percentages (for example, 0.25 and 25 both mean 25%). This scales configured position sizing and is not a share or contract count."
                    label="Size Fraction / Percent"
                    min={0}
                    step={0.01}
                    value={parseNumericDraft(state.literalValue)}
                    onValueChange={(numericValue) =>
                        onChange({
                            ...state,
                            literalValue: toNumericDraft(numericValue)
                        })
                    }
                />
            ) : (
                <div className="hidden md:block" />
            )}
            <div className="flex items-end">
                <Button size="sm" variant="outline-dark" onClick={onUseFullSize}>
                    Use Full Size
                </Button>
            </div>
        </div>
        {state.mode === 'json' && (
            <JsonObjectTextArea
                label="Size Expression JSON"
                value={state.jsonValue}
                onChange={(jsonValue) =>
                    onChange({
                        ...state,
                        jsonValue
                    })
                }
            />
        )}
    </div>
);

const ApplyEditorButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <div className="mt-3">
        <Button size="sm" variant="secondary" onClick={onClick}>
            {label}
        </Button>
    </div>
);

const EditorError = ({ message }: { message: string | null }) => {
    if (!message) return null;

    return <div className="mt-3 rounded-md border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-100">{message}</div>;
};

const EditorNotice = ({ message }: { message: string | null }) => {
    if (!message) return null;

    return <div className="mt-3 rounded-md border border-emerald-900/60 bg-emerald-950/30 p-2 text-xs text-emerald-100">{message}</div>;
};

const JsonObjectTextArea = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
    <div className="mt-3">
        <label className="mb-1 block text-sm font-medium text-zinc-300">{label}</label>
        <textarea
            className="min-h-32 w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 font-mono text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-p314-blue"
            spellCheck={false}
            value={value}
            onChange={(event) => onChange(event.target.value)}
        />
    </div>
);

const ReadOnlyJsonTextArea = ({ label, value }: { label: string; value: string }) => (
    <div className="mt-3">
        <label className="mb-1 block text-sm font-medium text-zinc-300">{label}</label>
        <textarea
            readOnly
            className="min-h-32 w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 font-mono text-sm text-zinc-100 opacity-90"
            spellCheck={false}
            value={value}
        />
    </div>
);

const numberToInputString = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

const jsonValueToTextAreaString = (value: unknown): string => (value === undefined ? '' : JSON.stringify(value, null, 2));

const parseOptionalJsonValue = (rawValue: string, label: string): TradeLogicJsonValue | undefined => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') return undefined;

    try {
        return JSON.parse(trimmedValue) as TradeLogicJsonValue;
    } catch {
        throw new Error(`${label} must be valid JSON or blank.`);
    }
};

const parseOptionalJsonObject = (rawValue: string, label: string): TradeLogicJsonObject | null => {
    const parsedValue = parseOptionalJsonValue(rawValue, label);

    if (parsedValue === undefined) return null;

    if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
        throw new Error(`${label} must be a JSON object or blank.`);
    }

    return parsedValue;
};

const parseOptionalContextFeatureRef = (rawValue: string): TradeLogicContextFeatureRef | null => {
    const parsedValue = parseOptionalJsonObject(rawValue, 'Feature Ref JSON');

    if (parsedValue === null) return null;

    if (parsedValue.kind !== 'contextFeature' || typeof parsedValue.featureKey !== 'string' || parsedValue.featureKey.trim() === '') {
        throw new Error('Feature Ref JSON must include kind "contextFeature" and a non-empty featureKey.');
    }

    return parsedValue as TradeLogicJsonObject & TradeLogicContextFeatureRef;
};

const optionalStringParameterToInputString = (value: TradeLogicJsonValue | undefined): string => (typeof value === 'string' ? value : '');

const parseOptionalNumber = (rawValue: string, label: string): number | null => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') return null;

    const parsed = Number(trimmedValue);

    if (!Number.isFinite(parsed)) {
        throw new Error(`${label} must be numeric`);
    }

    return parsed;
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <div className="rounded-md border border-zinc-800 bg-black/20 px-3 py-2">
        <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="mt-1 break-words text-sm text-zinc-200">{value}</div>
    </div>
);

const formatFeatureRefValidationState = (state: NonNullable<NodeInspectorPanelProps['featureRefValidationState']>) => {
    switch (state) {
        case 'checking':
            return 'Checking';
        case 'valid':
            return 'Valid';
        case 'invalid':
            return 'Invalid';
        case 'error':
            return 'Unavailable';
        case 'idle':
        default:
            return 'Not checked';
    }
};

export default NodeInspectorPanel;
